/**
 * La recette du balayage des squelettes : le surligneur de la marque qui passe sur la page.
 *
 * Des nombres, hors thème, comme les autres effets (`theme/tokens/effects.ts`, entrée « Les effets
 * sont des nombres hors thème » de `DECISIONS.md`). Ils vivent ici tant que la recette est
 * `proposé` (entrée du 28/09) ; validée, elle rejoint `tokens/effects` à côté du surligneur.
 *
 * Ce qui ne s'invente pas ici : la couleur (le jeton `skeleton-sweep` — le jaune du surligneur en
 * clair, le blanc sur la nuit), l'inclinaison (celle de `<Highlight>`) et la courbe
 * (`theme.motion.easing`).
 */
import { highlighter } from "../../theme/tokens/effects"

export const skeletonSweep = {
    /**
     * Un tour de boucle, en ms. Le trait suit la courbe de la marque, une décélération franche :
     * il traverse la page en ralentissant pendant le premier cinquième du tour, et la fin de la
     * courbe — la plus lente — se joue hors champ, à droite. C'est elle, le repos entre deux
     * passages : aucune pause à ajouter, et aucun bloc de droite ne reste allumé plus longtemps
     * que les autres.
     */
    duration: 4200,
    /** Jusqu'où le trait file au-delà du bord droit, en fraction de la largeur de la page. */
    overshoot: 0.5,
    /** La longueur du trait, en fraction de la largeur de la page, bornée en px. */
    length: { ratio: 0.45, min: 140, max: 420 },
    /**
     * L'opacité du trait. En clair, il ne prend que le bas de la barre : il peut porter un jaune
     * franc. Sur la nuit il couvre la barre entière, comme le surligneur couvre le mot, en blanc à
     * 8 % — le reflet d'un marqueur sur la nuit, pas une tache.
     */
    alpha: { day: 0.3, night: 0.08 },
    /** Le haut de la bande sur une ligne de texte, en clair : le bas du mot, comme `<Highlight>`. */
    underlineTop: highlighter.day.top,
    /** L'inclinaison du trait, en degrés — celle du surligneur. */
    rotate: highlighter.rotate,
} as const

/** La longueur du trait pour une page de cette largeur. */
export function sweepLength(pageWidth: number): number {
    const { ratio, min, max } = skeletonSweep.length
    return Math.min(max, Math.max(min, pageWidth * ratio))
}
